import type { ApiSymbolRow, ChartSymbol } from '../../types/api';
import { fetchSymbols } from '../../lib/api/symbols';

let symbols: Record<string, ChartSymbol> = {};
let symbolsPromise: Promise<Record<string, ChartSymbol>> | null = null;

export async function loadSymbols(force = false): Promise<Record<string, ChartSymbol>> {
  if (!force && symbolsPromise) {
    return symbolsPromise;
  }

  symbolsPromise = fetchSymbols({ limit: 200 }).then((rows) => {
    mergeSymbols(rows);
    return symbols;
  });

  return symbolsPromise;
}

export async function searchSymbols(query: string, options: { exchange?: string; type?: string; limit?: number } = {}): Promise<ChartSymbol[]> {
  const rows = await fetchSymbols({
    q: query,
    exchange: options.exchange,
    assetType: options.type,
    limit: options.limit ?? 60,
  });
  mergeSymbols(rows);
  return rows.map(normalizeSymbol);
}

export async function ensureSymbol(symbolName: string): Promise<ChartSymbol | null> {
  const cached = findSymbol(symbolName);
  if (cached) return cached;
  const raw = symbolName.trim();
  const name = raw.includes(':') ? raw.split(':').pop() ?? raw : raw;
  const matches = await searchSymbols(name, { limit: 10 });
  return matches.find((symbol) => (
    symbol.name.toLowerCase() === name.toLowerCase()
    || symbol.fullName.toLowerCase() === raw.toLowerCase()
  )) ?? null;
}

export function getLoadedSymbols(): Record<string, ChartSymbol> {
  return symbols;
}

function mergeSymbols(rows: ApiSymbolRow[]): Record<string, ChartSymbol> {
  for (const row of rows) {
    const symbol = normalizeSymbol(row);
    symbols[symbol.name] = symbol;
  }
  return symbols;
}

export function findSymbol(symbolName: string): ChartSymbol | null {
  const raw = symbolName.trim();
  if (!raw) return null;

  const name = raw.includes(':') ? raw.split(':').pop() ?? raw : raw;
  return symbols[raw]
    || symbols[name]
    || Object.values(symbols).find((symbol) => (
      symbol.name.toLowerCase() === name.toLowerCase()
      || symbol.fullName.toLowerCase() === raw.toLowerCase()
    ))
    || null;
}

export function normalizeSymbol(row: ApiSymbolRow): ChartSymbol {
  const assetType = (row.asset_type || '').toLowerCase();
  let type: ChartSymbol['type'] = 'crypto';
  let session = '24x7';
  let pricescale = 100;

  switch (assetType) {
    case 'stock':
    case 'equity':
      type = 'stock';
      session = '0930-1600';
      break;
    case 'forex':
      type = 'forex';
      pricescale = 100000;
      break;
  }

  return {
    name: row.symbol,
    fullName: `${row.exchange || 'Local'}:${row.symbol}`,
    description: row.description || row.name || row.symbol,
    exchange: row.exchange || 'Local',
    type,
    session,
    pricescale: row.price_scale && row.price_scale > 0 ? row.price_scale : pricescale,
  };
}
